import { untrack } from "svelte";

// Server `lastReadAt` (epoch ms) per channel and DM peer, seeded from `fetch_unread`: the source the
// "New messages" divider freezes from. Separate from the badge. A broadcast advance is monotonic
// (MM-44900); a reseed can lower it, unless it predates a local move.
type CursorMap = Record<string, number>;
export class ReadCursorState {
  private _channel: Record<string, number> = $state({});
  private _dm: Record<string, number> = $state({});
  // The server's DM cursors have arrived (an open frozen before that reads a cursor that is not there yet).
  dmSeeded = $state(false);
  private _dmWs: string | undefined; // the workspace the DM map holds
  private _channelWs: string | undefined;

  // When this device last moved each cursor (seed's race guard, the same idiom as
  // UnreadFlagState._markedAt). Bookkeeping only, so not $state.
  private _channelAt: CursorMap = {};
  private _dmAt: CursorMap = {};
  private _rewound = new Set<string>(); // last move was a rewind: its stamp alone guards it
  private receiptSettledAt: (scope: string) => number | undefined = () => undefined;

  bindReadReceipts(settledAt: (scope: string) => number | undefined): void {
    this.receiptSettledAt = settledAt;
  }

  // Seed from server truth; absent or null = not reported. Server truth wins, lower included (a
  // rewind this device missed), except a cursor this device moved at or after `seededAt` (the
  // clock read before the fetch went out, default now) or whose read receipt was unacked by then
  // (relay frames run concurrently): the snapshot may predate it. Another workspace replaces.
  seed(args: {
    reads?: CursorMap | null;
    dmReads?: CursorMap | null;
    wsId?: string;
    seededAt?: number;
  }): void {
    const seededAt = args.seededAt ?? Date.now();
    if (args.reads != null) {
      if (this.otherWs(this._channelWs, args.wsId)) {
        this._channelAt = {};
        this._rewound.clear();
      }
      this._channelWs = args.wsId ?? this._channelWs;
      const acked = this.ackedAt(`c:${this._channelWs}:`);
      const held = (k: string) => (this._rewound.has(k) ? 0 : acked(k));
      this._channel = this.mergeSeed(this._channel, args.reads, this._channelAt, seededAt, held);
    }
    if (args.dmReads == null) return;
    if (this.otherWs(this._dmWs, args.wsId)) this._dmAt = {};
    this._dmWs = args.wsId ?? this._dmWs;
    const ackedDm = this.ackedAt(`d:${this._dmWs}:`);
    this._dm = this.mergeSeed(this._dm, args.dmReads, this._dmAt, seededAt, ackedDm);
    this.dmSeeded = true;
  }

  private ackedAt(prefix: string): (k: string) => number {
    return (k) => this.receiptSettledAt(prefix + k) ?? 0;
  }

  private otherWs(heldWs?: string, wsId?: string): boolean {
    return !!heldWs && !!wsId && wsId !== heldWs;
  }

  // `at` is mutated in place: survivors are clamped to `seededAt` (clock step-back), the rest pruned.
  private mergeSeed(
    cur: CursorMap,
    incoming: CursorMap,
    at: CursorMap,
    seededAt: number,
    ackedAt: (k: string) => number,
  ): CursorMap {
    const local = untrack(() => cur);
    const next: CursorMap = { ...incoming };
    for (const [k, stamp] of Object.entries(at)) {
      if (Math.max(stamp, ackedAt(k)) >= seededAt && local[k] !== undefined) {
        next[k] = local[k];
        at[k] = Math.min(stamp, seededAt);
      } else delete at[k];
    }
    return next;
  }

  getChannel(channelId: string): number | null {
    return this._channel[channelId] ?? null;
  }

  getDm(peerId: string): number | null {
    return this._dm[peerId] ?? null;
  }

  // The `existing` reads below are UNTRACKED on purpose. An $effect that advances
  // a cursor would otherwise take a dependency on the very map it is about to
  // write: in runes mode Svelte does NOT skip the currently-running effect when a
  // source it read is written (sources.js `if (!runes && reaction === active_effect)
  // continue;`), so the effect self-invalidates. It also made every cursor advance
  // from ANYWHERE — notably the cross-device `read_broadcast` handler — re-run every
  // effect that had ever advanced a cursor, which is what closed the tab-to-tab
  // read-cursor loop that produced 296 dm_reads upserts in two minutes on ONE
  // conversation.
  //
  // Untracking the read here fixes every caller, present and future, instead of one
  // call site. RENDER paths must keep tracking, so getChannel/getDm above are
  // deliberately left alone — they are what the unread divider reads.
  advanceChannel(channelId: string, ms: number): void {
    const existing = untrack(() => this._channel[channelId]) ?? 0;
    if (ms <= existing) return;
    this._channelAt[channelId] = Date.now();
    this._rewound.delete(channelId);
    this._channel = { ...this._channel, [channelId]: ms };
  }

  // The one LIVE way a cursor goes backward: an explicit server rewind (mark-unread, read_broadcast
  // `rewind: true`). Everything else stays advance-only.
  setChannel(channelId: string, ms: number): void {
    this._channelAt[channelId] = Date.now();
    this._rewound.add(channelId);
    this._channel = { ...untrack(() => this._channel), [channelId]: ms };
  }

  advanceDm(peerId: string, ms: number): void {
    const existing = untrack(() => this._dm[peerId]) ?? 0;
    if (ms <= existing) return;
    this._dmAt[peerId] = Date.now();
    this._dm = { ...this._dm, [peerId]: ms };
  }

  reset(): void {
    this._channel = {};
    this._dm = {};
    this._channelAt = {};
    this._dmAt = {};
    this._rewound.clear();
    this._dmWs = undefined;
    this._channelWs = undefined;
    this.dmSeeded = false;
  }
}
